// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { canEnterDataPoints, isReadOnlyTeacherDemo } from '../utils/demoAccess';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { fetchTeacherSubjectsWithStudents, fetchStudentProgressionGrid, fetchAdminStudentDetail, fetchStudentProgressWorkbook, fetchStudentProgressPdf, fetchParentStudents, getMyStandardsViewPreference, saveMyStandardsViewPreference, listStandardsScopeTimeFrames, getEffectiveStandardsVisibility } from '../services/api';
import { sortStudentsByName, useStudentNameOrder } from '../utils/studentSort';
import './Teacher/StandardGradeEntry.css';
import BackToTop from '../components/BackToTop';
import PageBackNavigation from '../components/PageBackNavigation';
import StandardSearch, { matchesStandardSearch } from '../components/StandardSearch';
import StandardSetModal from '../components/StandardSetModal';
import { normalizedGradeLabel, normalizeLiteracyWritingStrand, resolveClusterLabel, resolveStrandLabel, summaryStatus } from '../utils/progressStatus';
import { fetchWithRetry } from '../utils/requestRetry';
import { dataPointTypeLabel } from '../utils/dataPointType';
import { GRADING_STYLES, pointColor } from '../utils/grading';
import GradingLegend from '../components/GradingLegend';
import AdditionalSupportIndicator, { ADDITIONAL_SUPPORT_COLOR, ADDITIONAL_SUPPORT_LABEL } from '../components/AdditionalSupportIndicator';
import {
  buildGradeColumns, findGradeColumnIndex, formatGradeColumnLabel, getStandardApplicableGrades, getStandardGradeColumnKey,
} from '../utils/gradeColumns';

const SUBJECT_ORDER = ['Mathematics', 'Science', 'Success Skills', 'Social Studies', 'Literacy'];

function getSubjectShortLabel(subject) {
  return subject === 'Mathematics' ? 'Math' : subject;
}

// Range grade strings are appended after their constituent individual grades so
// standards with ranges (6-8, 9-10, 11-12, 9-12) have a defined column position.
const GRADE_ORDER = ['PK', 'K', '1', '2', '3', '4', '5', '6', '7', '8', '6-8', '9', '10', '9-10', '11', '12', '11-12', '9-12'];
const GRADE_BANDS = [
  { label: 'PK', grades: ['PK'] },
  { label: 'K-5', grades: ['K', '1', '2', '3', '4', '5', 'K-2', '3-5'] },
  { label: '6-8', grades: ['6', '7', '8', '6-8'] },
  { label: '9-12', grades: ['9', '10', '11', '12', '9-10', '11-12', '9-12'] },
];
const CORE_GRADE_ORDER = ['PK', 'K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

// Set to true when the mastery gate should be re-enabled.
const ENABLE_GRADE_MASTERY_LOCK = false;
function normalizeKey(value) {
  return String(value || '').trim().toLowerCase();
}

function getGradeBand(grade) {
  const normalizedGrade = String(grade ?? '').toUpperCase();
  return GRADE_BANDS.find((band) => band.grades.includes(normalizedGrade)) || null;
}

function normalizeGradeToken(grade) {
  const normalized = String(grade ?? '').trim().toUpperCase();
  return normalized.replace(/^(\d+)\s+TO\s+(\d+)$/i, '$1-$2');
}

function getGradeFloor(grade) {
  const token = normalizeGradeToken(grade);
  if (!token) return '';
  if (token.includes('-')) {
    const startGrade = token.split('-')[0]?.trim() || '';
    return CORE_GRADE_ORDER.includes(startGrade) ? startGrade : '';
  }
  return CORE_GRADE_ORDER.includes(token) ? token : '';
}

function getGradeIndex(grade) {
  const baseGrade = getGradeFloor(grade);
  return CORE_GRADE_ORDER.indexOf(baseGrade);
}

function standardAppliesToGrade(standard, grade) {
  const [start, end = start] = normalizeGradeToken(standard.grade).split('-').map(token => token.trim());
  const rank = CORE_GRADE_ORDER.indexOf(grade);
  const startRank = CORE_GRADE_ORDER.indexOf(start);
  const endRank = CORE_GRADE_ORDER.indexOf(end);
  return rank >= 0 && startRank >= 0 && endRank >= startRank && rank >= startRank && rank <= endRank;
}

function getProfileColumnKey(standard) {
  return getStandardGradeColumnKey(standard.grade, standard.mls_code);
}

function getStandardAssessmentStatus(std) {
  if (std.status === 'proficient' || std.color === 'green') return 'green';
  if (std.status === 'on_track' || std.color === 'lightgreen') return 'lightgreen';
  if (std.status === 'working_level' || std.color === 'yellow') return 'yellow';
  if (std.status === 'not_yet' || std.color === 'red') return 'red';
  return 'unassessed';
}

function calculateAssessmentStats(standardsList = []) {
  let green = 0;
  let yellow = 0;
  let lightGreen = 0;
  let red = 0;
  let unassessed = 0;

  standardsList.forEach((std) => {
    const status = getStandardAssessmentStatus(std);
    if (status === 'green') green += 1;
    else if (status === 'lightgreen') lightGreen += 1;
    else if (status === 'yellow') yellow += 1;
    else if (status === 'red') red += 1;
    else unassessed += 1;
  });

  const assessed = green + lightGreen + yellow + red;
  const total = standardsList.length;
  const mastered = green;
  const masteryPercent = total > 0 ? Math.round((mastered / total) * 100) : 0;

  return { green, lightGreen, yellow, red, unassessed, assessed, mastered, total, masteryPercent };
}

function assessmentSummaryStyle(standards) {
  const stats = calculateAssessmentStats(standards);
  const status = summaryStatus({ counts: {
    working: stats.yellow, not_yet: stats.red, on_track: stats.lightGreen, mastered: stats.green,
  } });
  const color = { working: 'yellow', not_yet: 'red', on_track: 'lightgreen', mastered: 'green', na: 'gray' }[status];
  return GRADING_STYLES[color];
}

function buildSubjectGradeMasteryLookup(categories) {
  const lookup = {};

  categories.forEach((cat) => {
    (cat.skills || []).forEach((skill) => {
      skill.standards.forEach((standard) => {
        const subjectKey = normalizeKey(standard.subject || cat.subject || 'subject');
        const gradeKey = getGradeFloor(standard.grade);
        if (!subjectKey || !gradeKey) return;

        if (!lookup[subjectKey]) lookup[subjectKey] = {};
        if (!lookup[subjectKey][gradeKey]) {
          lookup[subjectKey][gradeKey] = { total: 0, mastered: 0 };
        }

        lookup[subjectKey][gradeKey].total += 1;
        if (standard.status === 'proficient') {
          lookup[subjectKey][gradeKey].mastered += 1;
        }
      });
    });
  });

  return lookup;
}

function getAssessmentGate(standard, studentGrade, masteryLookup) {
  if (!ENABLE_GRADE_MASTERY_LOCK) {
    return { locked: false, reason: '' };
  }

  const studentGradeIndex = getGradeIndex(studentGrade);
  const standardGradeIndex = getGradeIndex(standard.grade);
  if (studentGradeIndex < 0 || standardGradeIndex < 0 || standardGradeIndex <= studentGradeIndex) {
    return { locked: false, reason: '' };
  }

  const priorGrade = CORE_GRADE_ORDER[standardGradeIndex - 1];
  const subjectKey = normalizeKey(standard.subject || 'subject');
  const priorStats = masteryLookup?.[subjectKey]?.[priorGrade];
  if (!priorStats || priorStats.total === 0) {
    return { locked: false, reason: '' };
  }

  const masteryPercent = Math.round((priorStats.mastered / priorStats.total) * 100);
  if (masteryPercent >= 50) {
    return { locked: false, reason: '' };
  }

  return {
    locked: true,
    reason: `Assessment locked. ${priorGrade} must reach at least 50% mastery first (currently ${masteryPercent}%).`,
  };
}

function getInitials(firstName, lastName, fallback = '?') {
  const first = String(firstName || '').trim();
  const last = String(lastName || '').trim();
  return `${first[0] || fallback[0] || '?'}${last[0] || ''}`.toUpperCase();
}


function mapTileColors(skills) {
  const mappedSkills = skills.map((skill) => {
    const sorted = [...skill.standards].sort((a, b) => {
      const ai = GRADE_ORDER.indexOf(String(a.grade));
      const bi = GRADE_ORDER.indexOf(String(b.grade));
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
    const mapped = sorted.map((std) => {
      const color = std.color || null;
      let status;
      if (color === 'green') status = 'proficient';
      else if (color === 'lightgreen') status = 'on_track';
      else if (color === 'yellow') status = 'working_level';
      else if (color === 'red') status = 'not_yet';
      else status = 'not_assessed';
      return { ...std, status, color, inferred: false };
    });

    return { ...skill, standards: mapped };
  });

  const domainStandards = mappedSkills.flatMap((skill) => skill.standards);
  const gradeToOrderIndex = new Map();
  domainStandards.forEach((standard) => {
    const grade = String(standard.grade);
    gradeToOrderIndex.set(grade, GRADE_ORDER.indexOf(grade));
  });

  mappedSkills.forEach((skill) => {
    const gradeBuckets = new Map();
    skill.standards.forEach((standard) => {
      const gradeKey = String(standard.grade);
      if (!gradeBuckets.has(gradeKey)) {
        gradeBuckets.set(gradeKey, []);
      }
      gradeBuckets.get(gradeKey).push(standard);
    });

    const orderedGrades = [...gradeBuckets.keys()].sort((a, b) => {
      const ai = gradeToOrderIndex.get(String(a));
      const bi = gradeToOrderIndex.get(String(b));
      return (ai === undefined || ai === -1 ? 99 : ai) - (bi === undefined || bi === -1 ? 99 : bi);
    });

    for (let idx = orderedGrades.length - 1; idx >= 0; idx -= 1) {
      const currentGrade = orderedGrades[idx];
      const currentGradeStandards = gradeBuckets.get(currentGrade) || [];
      if (!currentGradeStandards.length) continue;

      const proficientCount = currentGradeStandards.filter((standard) => standard.status === 'proficient').length;
      const proficientRatio = proficientCount / currentGradeStandards.length;
      if (proficientRatio < 0.8) continue;

      const currentGradeIndex = gradeToOrderIndex.get(currentGrade);
      if (currentGradeIndex === undefined || currentGradeIndex < 0) continue;

      domainStandards.forEach((standard) => {
        const standardGradeIndex = gradeToOrderIndex.get(String(standard.grade));
        if (standardGradeIndex === undefined || standardGradeIndex < 0 || standardGradeIndex >= currentGradeIndex) {
          return;
        }
        if (standard.color !== null) return;

        standard.color = 'green';
        standard.status = 'proficient';
        standard.inferred = true;
      });
    }
  });

  return mappedSkills;
}

function buildProgressGrid(standardsWithData) {
  const categoriesMap = {};
  const seenMlsCodes = new Set();
  let subject = '';
  let studentName = '';
  let gradeLevel = '';
  standardsWithData.forEach(({ standard, data_point }) => {
    subject = standard.subject;
    // Skip duplicates — same MLS code may appear twice if DB has stale duplicates
    if (standard.mls_code && seenMlsCodes.has(standard.mls_code)) return;
    if (standard.mls_code) seenMlsCodes.add(standard.mls_code);
    const domainLabel = standard.domain_description || standard.domain || standard.subject || 'General';
    const catKey = `${standard.subject}::${domainLabel}`;
    if (!categoriesMap[catKey]) {
      categoriesMap[catKey] = {
        name: domainLabel,
        description: domainLabel,
        subject: standard.subject,
        skills: {},
      };
    }
    const sourceStrandLabel = resolveStrandLabel(standard, domainLabel);
    // Subjects such as Success Skills can omit strand text; fall back so the group still renders.
    const strandLabel = normalizeLiteracyWritingStrand(
      standard.subject,
      standard.grade_level,
      standard.mls_code,
      sourceStrandLabel,
    ) || sourceStrandLabel || domainLabel;
    const strandKey = strandLabel;

    if (!categoriesMap[catKey].skills[strandKey]) {
      categoriesMap[catKey].skills[strandKey] = {
        label: strandLabel,
        heading: null,
        standards: [],
      };
    }
    // Normalize legacy "X to Y" grade format so range columns are found in GRADE_ORDER
    const normalizedGrade = String(standard.grade_level ?? '')
      .replace(/^(\d+)\s+to\s+(\d+)$/i, '$1-$2');
    const pointSummary = data_point || {};
    const tileText = standard.standard_description
      || standard.cluster_expectation_description
      || standard.strand_description
      || '';
    categoriesMap[catKey].skills[strandKey].standards.push({
      grade: normalizedGrade,
      status: 'not_assessed',
      color: pointSummary.color || null,
      inferred: false,
      text: tileText,
      mls_code: standard.mls_code,
      avg_score: pointSummary.avg_score ?? null,
      data_points: Array.isArray(pointSummary.history) ? pointSummary.history : [],
      subject: standard.subject,
      domain: standard.domain,
      domain_description: standard.domain_description,
      strand: standard.strand,
      strand_description: standard.strand_description,
      cluster_and_expectation_number: standard.cluster_and_expectation_number || '',
      cluster_expectation_description: standard.cluster_expectation_description,
      standard_description: standard.standard_description,
    });
  });
  const categories = Object.values(categoriesMap).map(cat => ({
    ...cat,
    skills: Object.values(cat.skills),
  }));
  return { subject, studentName, gradeLevel, categories };
}

// TileColorPicker is kept here for future re-use when direct tile colour-marking
// is re-introduced. Not currently rendered (tile clicks open grade-entry instead).
// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
function TileColorPicker({ onSelect, onClose }: any) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.35)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: 'white', borderRadius: 16, padding: '32px 28px',
        minWidth: 320, boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700, textAlign: 'center', color: '#111' }}>
          Update Status
        </h2>
        {Object.entries(GRADING_STYLES).map(([color, style]) => ({ color, ...style })).map(opt => (
          <button key={opt.color} onClick={() => onSelect(opt.color)} style={{
            background: opt.bg, color: opt.text || 'white', border: 'none', borderRadius: 10,
            padding: '14px 0', fontSize: 16, fontWeight: 700, cursor: 'pointer', width: '100%',
          }}>
            {opt.label}
          </button>
        ))}
        <button onClick={onClose} style={{
          background: 'white', color: '#374151', border: '1.5px solid #d1d5db', borderRadius: 10,
          padding: '12px 0', fontSize: 16, fontWeight: 600, cursor: 'pointer', width: '100%',
          marginTop: 4,
        }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function StudentProgressPage({ role }: any) {
  const { studentId, classId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const returnTo = location.state?.returnTo || null;
  const returnLabel = returnTo?.label || '← Back';
  const restoredProgressState = location.state?.progressState;
  const [standardSearch, setStandardSearch] = useState(restoredProgressState?.standardSearch || '');
  const searchingStandards = Boolean(standardSearch.trim());
  const [progress, setProgress] = useState<any>(null);
  const [studentInfo, setStudentInfo] = useState<any>(null);
  const [loading, setLoading] = useState<any>(true);
  const [error, setError] = useState<any>(null);
  const [selectedGrade, setSelectedGrade] = useState(
    getGradeFloor(restoredProgressState?.selectedGrade) || null
  );
  const [selectedSubject, setSelectedSubject] = useState(restoredProgressState?.selectedSubject || location.state?.subject || '');
  const [viewMode, setViewMode] = useState('all');
  const [preferredTimeFrameType, setPreferredTimeFrameType] = useState<any>('');
  const [preferredTimeFrameLabel, setPreferredTimeFrameLabel] = useState<any>('');
  const [availableTimeFrames, setAvailableTimeFrames] = useState<any>([]);
  const [timeFramesUnavailableReason, setTimeFramesUnavailableReason] = useState<any>(null);
  const [effectiveVisibleCodes, setEffectiveVisibleCodes] = useState<any>(null);
  const [savingPreference, setSavingPreference] = useState<any>(false);
  const [preferenceError, setPreferenceError] = useState<any>('');
  const [isSetModalOpen, setIsSetModalOpen] = useState<any>(false);
  const [editingSet, setEditingSet] = useState<any>(null);
  const [selectedStandardsForEntry, setSelectedStandardsForEntry] = useState<any>([]);
  const [hoveredDataPoint, setHoveredDataPoint] = useState<any>(null);
  const dataPointHoverCloseTimer = useRef<any>(null);
  const hasLoadedProgressRef = useRef(false);
  const progressRequestRef = useRef<any>(0);
  const [profileClasses, setProfileClasses] = useState<any>([]);
  const [profileRosterLoading, setProfileRosterLoading] = useState<any>(false);
  const [profileRosterError, setProfileRosterError] = useState<any>('');
  const [profileClassId, setProfileClassId] = useState<any>('');
  const [nameOrder] = useStudentNameOrder();

  const cancelDataPointHoverClose = useCallback(() => {
    if (dataPointHoverCloseTimer.current) {
      window.clearTimeout(dataPointHoverCloseTimer.current);
      dataPointHoverCloseTimer.current = null;
    }
  }, []);

  const scheduleDataPointHoverClose = useCallback(() => {
    cancelDataPointHoverClose();
    dataPointHoverCloseTimer.current = window.setTimeout(() => {
      setHoveredDataPoint(null);
      dataPointHoverCloseTimer.current = null;
    }, 450);
  }, [cancelDataPointHoverClose]);

  useEffect(() => () => cancelDataPointHoverClose(), [cancelDataPointHoverClose]);

  const activeCustomSet = useMemo(() => {
    if (viewMode !== 'preferred' || !preferredTimeFrameType || !preferredTimeFrameLabel) return null;
    return availableTimeFrames.find(
      (tf) => tf.is_custom && tf.time_frame_type === preferredTimeFrameType && tf.time_frame_label === preferredTimeFrameLabel
    ) || null;
  }, [viewMode, preferredTimeFrameType, preferredTimeFrameLabel, availableTimeFrames]);

  const handleSetModalSaved = async (savedSet) => {
    setIsSetModalOpen(false);
    setEditingSet(null);
    setSavingPreference(true);
    setPreferenceError('');
    try {
      await saveMyStandardsViewPreference({
        viewMode: 'preferred',
        timeFrameType: savedSet.time_frame_type,
        timeFrameLabel: savedSet.time_frame_label,
      });
      setViewMode('preferred');
      setPreferredTimeFrameType(savedSet.time_frame_type);
      setPreferredTimeFrameLabel(savedSet.time_frame_label);

      const tfData = await listStandardsScopeTimeFrames({ subject: selectedSubject, gradeBand: activeGradeBand.label });
      setAvailableTimeFrames(tfData.time_frames || []);

      const effData = await getEffectiveStandardsVisibility({ subject: selectedSubject, gradeBand: activeGradeBand.label });
      if (effData.mode === 'preferred' && Array.isArray(effData.mls_codes)) {
        setEffectiveVisibleCodes(new Set(effData.mls_codes));
      } else {
        setEffectiveVisibleCodes(null);
      }
    } catch (e: any) {
      setPreferenceError(e.message || 'Failed to update standard set view');
    } finally {
      setSavingPreference(false);
    }
  };

  const handleSetModalDeleted = async () => {
    setIsSetModalOpen(false);
    setEditingSet(null);
    try {
      await saveMyStandardsViewPreference({ viewMode: 'all', timeFrameType: null, timeFrameLabel: null });
      setViewMode('all');
      setPreferredTimeFrameType('');
      setPreferredTimeFrameLabel('');
      setEffectiveVisibleCodes(null);

      const tfData = await listStandardsScopeTimeFrames({ subject: selectedSubject, gradeBand: activeGradeBand.label });
      setAvailableTimeFrames(tfData.time_frames || []);
    } catch (_e: any) {}
  };
  const { user } = useAuth();
  const readOnlyDemo = isReadOnlyTeacherDemo(user);
  const [expandedSkills, setExpandedSkills] = useState(restoredProgressState?.expandedSkills || {});
  const [expandedDomains, setExpandedDomains] = useState(restoredProgressState?.expandedDomains || {});
  const [showStandardCodes, setShowStandardCodes] = useState<any>(false);
  const isFamilyView = role === 'parent' || role === 'family';

  const effectiveRole = role || (user && user.role) || 'unknown';
  const canEdit = canEnterDataPoints(user) && (effectiveRole === 'teacher' || effectiveRole === 'special_services_teacher' || effectiveRole === 'admin' || effectiveRole === 'school_admin' || effectiveRole === 'district_admin');
  const teacherProfileNavigation = effectiveRole === 'teacher' || effectiveRole === 'special_services_teacher';

  useEffect(() => {
    if (!teacherProfileNavigation) return undefined;
    let cancelled = false;
    setProfileRosterLoading(true);
    setProfileRosterError('');
    fetchTeacherSubjectsWithStudents().then(data => {
      if (cancelled) return;
      const classes = new Map();
      (Array.isArray(data) ? data : data?.subjects || []).forEach(subject => {
        (subject.classes || []).forEach(cls => {
          const id = String(cls.class_id);
          const existing = classes.get(id);
          const students = new Map((existing?.students || []).map(student => [String(student.student_id), student]));
          (cls.students || []).forEach(student => students.set(String(student.student_id), student));
          classes.set(id, { ...cls, class_id: id, students: [...students.values()] });
        });
      });
      setProfileClasses([...classes.values()]);
    }).catch(err => {
      if (!cancelled) setProfileRosterError(err.message || 'Could not load class students. Please reload to try again.');
    }).finally(() => { if (!cancelled) setProfileRosterLoading(false); });
    return () => { cancelled = true; };
  }, [teacherProfileNavigation]);

  const studentClasses = profileClasses.filter(cls => cls.students.some(student => String(student.student_id) === String(studentId)));
  const navigationClass = studentClasses.find(cls => cls.class_id === String(profileClassId || classId || location.state?.profileClassId))
    || studentClasses[0];
  const navigationStudents = sortStudentsByName(navigationClass?.students || [], nameOrder);
  const navigationIndex = navigationStudents.findIndex(student => String(student.student_id) === String(studentId));
  const switchProfileStudent = (student) => {
    if (!student || !navigationClass || String(student.student_id) === String(studentId)) return;
    navigate(`/teacher/student-progress/${student.student_id}${classId ? `/${navigationClass.class_id}` : ''}`, {
      state: {
        student, subject: selectedSubject, profileClassId: navigationClass.class_id,
        returnTo: returnTo ? { ...returnTo, state: { ...returnTo.state, selectedStudentId: student.student_id } } : undefined,
      },
    });
  };

  useEffect(() => {
    hasLoadedProgressRef.current = false;
    setProgress(null);
    setError(null);
    setSelectedGrade(getGradeFloor(restoredProgressState?.selectedGrade) || null);
    setSelectedStandardsForEntry([]);
    setHoveredDataPoint(null);
    setExpandedDomains(restoredProgressState?.expandedDomains || {});
    setExpandedSkills(restoredProgressState?.expandedSkills || {});
    setStandardSearch(restoredProgressState?.standardSearch || '');
    // Reset student-specific selections only when changing students.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const restoredScrollRef = useRef(false);
  useEffect(() => {
    if (loading || !progress || !restoredProgressState || restoredScrollRef.current) return undefined;
    const frame = window.requestAnimationFrame(() => {
      window.scrollTo({ top: restoredProgressState.scrollY || 0, behavior: 'auto' });
      restoredScrollRef.current = true;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [loading, progress, restoredProgressState]);

  useEffect(() => {
    let cancelled = false;
    const stateStudent = location.state?.student;
    setStudentInfo(null);
    if (stateStudent) {
      setStudentInfo(stateStudent);
    } else if (studentId) {
      if (isFamilyView) {
        fetchParentStudents()
          .then((data) => {
            if (cancelled) return;
            const match = (data?.students || []).find((student) => String(student.student_id) === String(studentId));
            if (match) {
              setStudentInfo(match);
            }
          })
          .catch(() => {});
      } else {
        fetchAdminStudentDetail(studentId).then(data => { if (!cancelled) setStudentInfo(data); }).catch(() => {});
      }
    }
    return () => { cancelled = true; };
  }, [studentId, location.state, isFamilyView]);

  const loadProgressData = useCallback(async ({ silent = false } = {}) => {
    if (!studentId) return;
    const request = ++progressRequestRef.current;
    if (!silent) setLoading(true);
    try {
      const gridData = await fetchWithRetry(() => fetchStudentProgressionGrid(studentId));
      if (request !== progressRequestRef.current) return;
      setProgress(buildProgressGrid(gridData));
      hasLoadedProgressRef.current = true;
      setError(null);
    } catch (_err: any) {
      if (request !== progressRequestRef.current) return;
      // Keep the last good grid visible instead of blanking on a transient failure.
      if (!hasLoadedProgressRef.current) {
        setError('Failed to load student progression data.');
      }
    } finally {
      if (!silent && request === progressRequestRef.current) setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    loadProgressData();
    return () => { progressRequestRef.current += 1; };
  }, [loadProgressData]);

  useEffect(() => {
    if (!isFamilyView) {
      return undefined;
    }

    const refreshOnFocus = () => {
      if (document.visibilityState === 'visible') {
        loadProgressData({ silent: true });
      }
    };

    const refreshInterval = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadProgressData({ silent: true });
      }
    }, 30000);

    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshOnFocus);

    return () => {
      window.clearInterval(refreshInterval);
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshOnFocus);
    };
  }, [isFamilyView, loadProgressData]);

  const categoriesWithInference = progress
    ? progress.categories.map(cat => ({
        ...cat,
        skills: mapTileColors(cat.skills),
      }))
    : [];
  const masteryBySubjectGrade = buildSubjectGradeMasteryLookup(categoriesWithInference);

  const subjectsAvailable = Array.from(new Set(categoriesWithInference.map((cat) => cat.subject).filter(Boolean)))
    .sort((a, b) => {
      const leftIndex = SUBJECT_ORDER.indexOf(a);
      const rightIndex = SUBJECT_ORDER.indexOf(b);
      if (leftIndex === -1 && rightIndex === -1) return a.localeCompare(b);
      if (leftIndex === -1) return 1;
      if (rightIndex === -1) return -1;
      return leftIndex - rightIndex;
    });

  useEffect(() => {
    if (!subjectsAvailable.length) return;
    if (!selectedSubject || !subjectsAvailable.includes(selectedSubject)) {
      setSelectedSubject(subjectsAvailable[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectsAvailable.join('|')]);

  const studentGrade = getGradeFloor(studentInfo?.grade_level ?? progress?.gradeLevel);
  const activeGrade = selectedGrade || studentGrade || getGradeFloor(restoredProgressState?.selectedBandLabel) || 'K';
  // Grade-band standards (e.g. 9-12) get one column, matching Data Entry and Family Progress.
  const profileGradeColumns = buildGradeColumns(
    categoriesWithInference
      .filter(cat => !selectedSubject || cat.subject === selectedSubject)
      .flatMap(cat => cat.skills.flatMap(skill => skill.standards))
      .map(standard => ({ applicableGrades: getStandardApplicableGrades(standard.grade, standard.mls_code) })),
    { includeUncoveredGrades: true },
  );
  const activeColumnIndex = Math.max(0, findGradeColumnIndex(profileGradeColumns, activeGrade));
  const columnWindowStart = Math.max(0, Math.min(profileGradeColumns.length - 3, activeColumnIndex - 1));
  const visibleProfileColumnKeys = profileGradeColumns.slice(columnWindowStart, columnWindowStart + 3).map(column => column.key);
  const focusedProfileColumnKey = profileGradeColumns[activeColumnIndex]?.key || activeGrade;
  const moveProfileGrade = (direction) => {
    const column = profileGradeColumns[Math.max(0, Math.min(profileGradeColumns.length - 1, activeColumnIndex + direction))];
    if (column) setSelectedGrade(column.grades.includes(activeGrade) ? activeGrade : column.grades[0]);
  };
  // Standard Sets remain band-scoped even though the profile displays one grade.
  const activeGradeBand = getGradeBand(activeGrade);
  const subjectGradeStyles = Object.fromEntries(subjectsAvailable.map((subject) => {
    const standards = categoriesWithInference
      .filter(cat => cat.subject === subject)
      .flatMap(cat => cat.skills.flatMap(skill => skill.standards))
      .filter(standard => standardAppliesToGrade(standard, activeGrade));
    return [subject, assessmentSummaryStyle(standards)];
  }));

  useEffect(() => {
    let cancelled = false;
    getMyStandardsViewPreference()
      .then((pref) => {
        if (cancelled) return;
        setViewMode(pref.view_mode || 'all');
        setPreferredTimeFrameType(pref.time_frame_type || '');
        setPreferredTimeFrameLabel(pref.time_frame_label || '');
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedSubject || !activeGradeBand?.label) {
      setAvailableTimeFrames([]);
      setTimeFramesUnavailableReason(null);
      return;
    }
    let cancelled = false;
    listStandardsScopeTimeFrames({ subject: selectedSubject, gradeBand: activeGradeBand.label })
      .then((data) => {
        if (cancelled) return;
        setAvailableTimeFrames(data.time_frames || []);
        setTimeFramesUnavailableReason(data.reason || null);
      })
      .catch(() => {
        if (!cancelled) {
          setAvailableTimeFrames([]);
          setTimeFramesUnavailableReason(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSubject, activeGradeBand?.label]);

  useEffect(() => {
    if (viewMode !== 'preferred' || !preferredTimeFrameType || !preferredTimeFrameLabel || !selectedSubject || !activeGradeBand?.label) {
      setEffectiveVisibleCodes(null);
      return;
    }
    let cancelled = false;
    getEffectiveStandardsVisibility({ subject: selectedSubject, gradeBand: activeGradeBand.label })
      .then((data) => {
        if (cancelled) return;
        setEffectiveVisibleCodes(data.mode === 'preferred' && Array.isArray(data.mls_codes) ? new Set(data.mls_codes) : null);
      })
      .catch(() => {
        if (!cancelled) setEffectiveVisibleCodes(null);
      });
    return () => {
      cancelled = true;
    };
  }, [viewMode, preferredTimeFrameType, preferredTimeFrameLabel, selectedSubject, activeGradeBand?.label]);

  const handleViewModeChange = async (nextMode) => {
    setPreferenceError('');
    setViewMode(nextMode);
    if (nextMode === 'all') {
      setPreferredTimeFrameType('');
      setPreferredTimeFrameLabel('');
      if (readOnlyDemo) return;
      setSavingPreference(true);
      try {
        await saveMyStandardsViewPreference({ viewMode: 'all', timeFrameType: null, timeFrameLabel: null });
      } catch (err: any) {
        setPreferenceError(err.message || 'Failed to save your view preference.');
      } finally {
        setSavingPreference(false);
      }
    }
  };

  const handleTimeFrameChange = async (value) => {
    const [type, label] = value.split('::');
    setPreferredTimeFrameType(type || '');
    setPreferredTimeFrameLabel(label || '');
    if (!type || !label || readOnlyDemo) return;
    setSavingPreference(true);
    setPreferenceError('');
    try {
      await saveMyStandardsViewPreference({ viewMode: 'preferred', timeFrameType: type, timeFrameLabel: label });
    } catch (err: any) {
      setPreferenceError(err.message || 'Failed to save your view preference.');
    } finally {
      setSavingPreference(false);
    }
  };

  const displayCategories = categoriesWithInference
    .filter((cat) => !selectedSubject || cat.subject === selectedSubject)
    .map((cat) => {
      const visibleSkills = cat.skills
        .map((skill) => {
          const setStandards = viewMode === 'preferred' && effectiveVisibleCodes
            ? skill.standards.filter((standard) => effectiveVisibleCodes.has(standard.mls_code))
            : skill.standards;
          const scopedStandards = setStandards.filter((standard) => visibleProfileColumnKeys.includes(getProfileColumnKey(standard))
            && matchesStandardSearch(standardSearch, [
            standard.mls_code, standard.standard_description, standard.text,
            cat.name, cat.description, skill.label, standard.strand_description,
          ]));

          const visibleCols = scopedStandards.length ? visibleProfileColumnKeys : [];
          const visibleStandards = scopedStandards;

          if (visibleCols.length === 0) {
            return null;
          }
          return {
            ...skill,
            standards: scopedStandards,
            visibleStandards,
            visibleCols,
            gradeRangeLabel: activeGrade,
          };
        })
        .filter(Boolean);

      if (!visibleSkills.length) {
        return null;
      }

      return {
        ...cat,
        visibleSkills,
      };
    })
    .filter(Boolean);

  const handleExportWorkbook = async () => {
    try {
      const blob = await fetchStudentProgressWorkbook(studentId, classId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const subjectName = String(selectedSubject || progress.subject || 'profile').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
      const studentName = String(studentInfo ? `${studentInfo.first_name || ''} ${studentInfo.last_name || ''}`.trim() : progress.studentName || 'student')
        .replace(/\s+/g, '_')
        .replace(/[^a-zA-Z0-9_-]/g, '');
      link.href = url;
      link.download = `${studentName || 'student'}_${subjectName || 'profile'}_progress_report.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (_error: any) {
      setError('Failed to export progress workbook.');
    }
  };

  const handleExportPdf = async () => {
    try {
      const blob = await fetchStudentProgressPdf(studentId, classId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const subjectName = String(selectedSubject || progress.subject || 'profile').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
      const studentName = String(studentInfo ? `${studentInfo.first_name || ''} ${studentInfo.last_name || ''}`.trim() : progress.studentName || 'student')
        .replace(/\s+/g, '_')
        .replace(/[^a-zA-Z0-9_-]/g, '');
      link.href = url;
      link.download = `${studentName || 'student'}_${subjectName || 'profile'}_family_report.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (_error: any) {
      setError('Failed to export PDF report.');
    }
  };

  const handleBack = () => {
    if (returnTo && typeof returnTo === 'object' && returnTo.pathname) {
      navigate(returnTo.pathname, returnTo.state ? { state: returnTo.state } : undefined);
      return;
    }

    if (role === 'teacher' || role === 'special_services_teacher') {
      navigate('/teacher/student-profiles');
      return;
    }

    if (role === 'admin' || role === 'school_admin' || role === 'district_admin') {
      navigate('/admin/student-profiles');
      return;
    }

    if (role === 'parent' || role === 'family') {
      navigate('/parent/student-profiles');
      return;
    }

    navigate('/dashboard');
  };

  const navigateToGradeEntry = (std, pointId = null, standards = [std]) => {
    if (!canEdit || !std) return;

    const isAdminPath = location.pathname.startsWith('/admin') || effectiveRole === 'admin' || effectiveRole === 'school_admin' || effectiveRole === 'district_admin';
    const targetPath = isAdminPath ? '/admin/grade-entry' : '/teacher/grade-entry';

    const selections = (standards || [std]).filter(Boolean).map((standard) => ({
      code: standard.mls_code,
      subject: standard.subject || progress?.subject || selectedSubject,
      domain: standard.domain || standard.domain_description || '',
      strand: standard.strand || standard.strand_description || '',
      strandDescription: normalizeLiteracyWritingStrand(
        standard.subject || progress?.subject || selectedSubject,
        standard.grade,
        standard.mls_code,
        standard.strand_description || standard.strand || '',
      ) || standard.strand_description || standard.strand || '',
      standardDescription: standard.standard_description || standard.text || '',
      clusterExpectationDescription: standard.cluster_expectation_description || '',
    }));
    const selection = selections[0];
    if (!selection) return;

    const studentName = studentInfo?.full_name || (studentInfo?.first_name ? `${studentInfo.first_name} ${studentInfo.last_name || ''}`.trim() : '') || progress?.studentName || `Student ${studentId}`;

    navigate(targetPath, {
      state: {
        selection,
        selections,
        classId: classId || progress?.class_id || '',
        preselectedStudentId: studentId ? Number(studentId) : null,
        profileStudent: {
          student_id: Number(studentId),
          full_name: studentName,
        },
        pointId: pointId || null,
        returnTo: {
          pathname: location.pathname,
          state: {
            ...location.state,
            progressState: {
              selectedGrade: activeGrade,
              selectedSubject,
              expandedDomains,
              expandedSkills,
              standardSearch,
              scrollY: window.scrollY,
            },
          },
          label: `← Back to ${studentName}'s Progress`,
        },
        progressionState: {
          grade: std.grade || '',
          subject: std.subject || progress?.subject || selectedSubject,
          domain: std.domain || '',
          strand: std.strand || '',
        },
      },
    });
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading student progress...</div>;
  if (error) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ color: 'red', marginBottom: 12 }}>{error}</div>
        <button
          type="button"
          onClick={() => loadProgressData()}
          style={{ border: '1px solid #cbd5e1', background: '#ffffff', color: '#1e3a5f', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
        >
          Try again
        </button>
      </div>
    );
  }
  if (!progress) return <div style={{ padding: 40, textAlign: 'center' }}>No progress data found.</div>;

  const studentDisplayName = studentInfo?.full_name || `${studentInfo?.first_name || ''} ${studentInfo?.last_name || ''}`.trim() || progress.studentName || `Student ${studentId}`;
  const studentInitials = getInitials(studentInfo?.first_name, studentInfo?.last_name, progress.studentName || 'S');
  const familyMembers = Array.isArray(studentInfo?.family_members) ? studentInfo.family_members.slice(0, 2) : [];
  const currentTeachers = Array.isArray(studentInfo?.classes)
    ? Array.from(new Map(
        studentInfo.classes
          .filter((cls) => cls.teacher_name)
          .map((cls) => [cls.teacher_id || cls.teacher_name, cls.teacher_name]),
      ).values())
    : [];

  return (
    <div style={{ background: '#ffffff', borderRadius: 18, boxShadow: '0 10px 32px rgba(15, 23, 42, 0.08)', padding: 20, border: '1px solid #dde6f2', overflowX: 'auto' }}>
      <PageBackNavigation onBack={handleBack} label={returnLabel} />
      {teacherProfileNavigation && <nav aria-label="Student profile navigation" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        {profileRosterError ? <p role="alert">{profileRosterError}</p> : <>
          {studentClasses.length > 1 && <label>Class{' '}
            <select aria-label="Profile class" value={navigationClass?.class_id || ''} onChange={event => setProfileClassId(event.target.value)}>
              {studentClasses.map(cls => <option key={cls.class_id} value={cls.class_id}>{cls.class_name || `Class ${cls.class_id}`}</option>)}
            </select>
          </label>}
          <button type="button" aria-label="Previous student" disabled={profileRosterLoading || navigationIndex <= 0}
            onClick={() => switchProfileStudent(navigationStudents[navigationIndex - 1])}>←</button>
          <label>Student{' '}
            <select aria-label="Profile student" value={navigationIndex >= 0 ? String(studentId) : ''} disabled={profileRosterLoading || !navigationStudents.length}
              onChange={event => switchProfileStudent(navigationStudents.find(student => String(student.student_id) === event.target.value))}>
              {!navigationStudents.length && <option value="">{profileRosterLoading ? 'Loading students…' : 'No accessible class roster'}</option>}
              {navigationStudents.map(student => <option key={student.student_id} value={String(student.student_id)}>
                {student.full_name || `${student.first_name || ''} ${student.last_name || ''}`.trim() || `Student ${student.student_id}`}
              </option>)}
            </select>
          </label>
          <button type="button" aria-label="Next student" disabled={profileRosterLoading || navigationIndex < 0 || navigationIndex >= navigationStudents.length - 1}
            onClick={() => switchProfileStudent(navigationStudents[navigationIndex + 1])}>→</button>
          {navigationClass && <span style={{ fontSize: 12, color: '#64748b' }}>{navigationClass.class_name} · {navigationIndex + 1} of {navigationStudents.length}</span>}
        </>}
      </nav>}
      <div style={{ marginBottom: 20 }}>
        <section aria-label="Student profile summary" style={{ marginBottom: 18, background: 'var(--ae-surface)', border: '1px solid var(--ae-border)', borderRadius: 18, padding: '18px 18px 14px', boxShadow: '0 12px 28px rgba(15, 23, 42, 0.06)' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center' }}>
            <div aria-hidden="true" style={{ width: 90, height: 90, flexShrink: 0, borderRadius: '50%', background: 'linear-gradient(180deg, #e6f0ff 0%, #cde0ff 100%)', border: '2px solid #bfd5f5', color: 'var(--ae-primary-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 30 }}>
              {studentInitials}
            </div>
            <div style={{ flex: '1 1 240px', minWidth: 0, background: '#3e5ea8', color: '#fff', borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                <h1 style={{ fontSize: 30, fontWeight: 800, color: '#fff', margin: 0, lineHeight: 1.1 }}>{studentDisplayName}</h1>
                <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#dbe7ff', marginTop: 4 }}>
                  {selectedSubject || progress.subject} · Student Profile
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.1 }}>
                  {normalizedGradeLabel(studentInfo?.grade_level ?? progress.gradeLevel ?? 'N/A')}
                </div>
                <div style={{ fontSize: 12, color: '#dbe7ff', marginTop: 3 }}>Progress Snapshot</div>
              </div>
            </div>
          </div>
        </section>
        <div style={{ marginTop: 14 }}>
          <div style={{ marginBottom: 8, fontSize: 12, fontWeight: 800, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Grade</div>
          <div role="group" aria-label="Grade" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {CORE_GRADE_ORDER.map((grade) => {
              const isActive = activeGrade === grade;
              return (
                <button
                  key={grade}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setSelectedGrade(grade)}
                  style={{
                    border: isActive ? '1px solid #2563eb' : '1px solid #cbd5e1',
                    background: isActive ? '#2563eb' : '#ffffff',
                    color: isActive ? '#ffffff' : '#334155',
                    borderRadius: 999,
                    padding: '8px 12px',
                    minHeight: 40,
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  {grade}
                </button>
              );
            })}
          </div>
        </div>
        {subjectsAvailable.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#475569', marginTop: 8 }}>Subject:</span>
            <div role="group" aria-label="Subject" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {subjectsAvailable.map((subject) => {
                const isActive = selectedSubject === subject;
                const statusStyle = subjectGradeStyles[subject];
                return (
                  <button
                    key={subject}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => setSelectedSubject(subject)}
                    title={`${subject} · Grade ${activeGrade} · ${statusStyle.label}`}
                    style={{
                      border: isActive ? '1px solid #2563eb' : '1px solid #cbd5e1',
                      borderRadius: 10,
                      background: statusStyle.bg,
                      color: statusStyle.text,
                      boxShadow: isActive ? '0 0 0 2px #2563eb' : 'none',
                      padding: '8px 14px',
                      minHeight: 40,
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    {getSubjectShortLabel(subject)}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginTop: 10, fontSize: 12 }}>
          <span style={{ fontWeight: 700, color: '#1e3a5f' }}>Standards View:</span>
          <button
            type="button"
            onClick={() => handleViewModeChange('all')}
            style={{
              padding: '4px 12px',
              borderRadius: 999,
              border: viewMode === 'all' ? '1px solid #1d4ed8' : '1px solid #cbd5e1',
              background: viewMode === 'all' ? '#1d4ed8' : '#fff',
              color: viewMode === 'all' ? '#fff' : '#334155',
              cursor: 'pointer',
              fontWeight: 700,
            }}
          >
            Standard (All)
          </button>
          <button
            type="button"
            onClick={() => handleViewModeChange('preferred')}
            style={{
              padding: '4px 12px',
              borderRadius: 999,
              border: viewMode === 'preferred' ? '1px solid #1d4ed8' : '1px solid #cbd5e1',
              background: viewMode === 'preferred' ? '#1d4ed8' : '#fff',
              color: viewMode === 'preferred' ? '#fff' : '#334155',
              cursor: 'pointer',
              fontWeight: 700,
            }}
          >
            Standard Set
          </button>
          {viewMode === 'preferred' && (
            <>
              <select
                value={preferredTimeFrameType && preferredTimeFrameLabel ? `${preferredTimeFrameType}::${preferredTimeFrameLabel}` : ''}
                onChange={(event) => handleTimeFrameChange(event.target.value)}
                style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, background: '#ffffff' }}
              >
                <option value="">Select standard set</option>
                {availableTimeFrames.map((timeFrame) => (
                  <option
                    key={`${timeFrame.time_frame_type}::${timeFrame.time_frame_label}`}
                    value={`${timeFrame.time_frame_type}::${timeFrame.time_frame_label}`}
                  >
                    {timeFrame.time_frame_type}: {timeFrame.time_frame_label}{timeFrame.is_custom ? ' (Custom)' : ''}
                  </option>
                ))}
              </select>
              {!readOnlyDemo && <button
                type="button"
                onClick={() => {
                  setEditingSet(null);
                  setIsSetModalOpen(true);
                }}
                style={{
                  padding: '4px 10px',
                  borderRadius: 999,
                  border: '1px solid #1d4ed8',
                  background: '#eff6ff',
                  color: '#1d4ed8',
                  cursor: 'pointer',
                  fontWeight: 800,
                  fontSize: 12,
                }}
              >
                + Create Standard Set
              </button>}
              {!readOnlyDemo && activeCustomSet && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingSet({
                      id: activeCustomSet.id,
                      subject: selectedSubject,
                      grade_band: activeGradeBand.label,
                      time_frame_type: activeCustomSet.time_frame_type,
                      time_frame_label: activeCustomSet.time_frame_label,
                      mls_codes: activeCustomSet.mls_codes || [],
                    });
                    setIsSetModalOpen(true);
                  }}
                  style={{
                    padding: '4px 10px',
                    borderRadius: 999,
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    color: '#334155',
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                >
                  ✏ Edit Set
                </button>
              )}
            </>
          )}
          {savingPreference && <span style={{ color: '#6b7280' }}>Saving…</span>}
          {viewMode === 'preferred' && !availableTimeFrames.length && timeFramesUnavailableReason === 'school_state_missing' && (
            <span style={{ color: '#9a3412' }}>
              Your school doesn't have a state assigned yet, so Standard Set view can't be configured. Ask your superadmin to set your school's state.
            </span>
          )}
          {viewMode === 'preferred' && !availableTimeFrames.length && timeFramesUnavailableReason !== 'school_state_missing' && (
            <span style={{ color: '#9a3412' }}>
              No standard sets created yet for {selectedSubject || 'this subject'} · {activeGradeBand?.label || ''}.{!readOnlyDemo && ' Click "+ Create Standard Set" to create one.'}
            </span>
          )}
          {preferenceError && <span style={{ color: '#b91c1c' }}>{preferenceError}</span>}
        </div>
        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>
          Showing Grades: <span style={{ fontWeight: 700, color: '#1e3a5f' }}>{visibleProfileColumnKeys.join(', ')}</span>
        </div>
      </div>

      <StandardSetModal
        isOpen={isSetModalOpen}
        onClose={() => setIsSetModalOpen(false)}
        initialSubject={selectedSubject}
        initialGradeBand={activeGradeBand?.label || 'K-5'}
        editingSet={editingSet}
        onSaved={handleSetModalSaved}
        onDeleted={handleSetModalDeleted}
      />

      {isFamilyView && studentInfo && (
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 18, alignItems: 'start', marginBottom: 18 }}>
          <div style={{ width: 120, minHeight: 120, borderRadius: 16, background: 'linear-gradient(180deg, #ffe0c6 0%, #ffd1ad 100%)', border: '1px solid #ffc089', padding: 10, boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#dbeafe', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1d4ed8', fontWeight: 800, fontSize: 26 }}>
              {studentInitials}
            </div>
            <div style={{ marginTop: 10, width: '100%', fontSize: 11, lineHeight: 1.35, color: '#9a3412', textAlign: 'left' }}>
              <div style={{ fontWeight: 800, color: '#1e3a5f' }}>{studentDisplayName}</div>
              <div>Grade {studentInfo.grade_level ?? progress.gradeLevel ?? 'N/A'}</div>
            </div>
          </div>

          <div style={{ padding: '12px 14px', borderRadius: 14, background: '#ffffff', border: '1px solid #dbe4f0', boxShadow: '0 10px 24px rgba(15, 23, 42, 0.07)' }}>
            <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 800, color: '#64748b', marginBottom: 6 }}>Student Profile</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#1e3a5f', marginBottom: 6 }}>{studentDisplayName}</div>
            <div style={{ fontSize: 13, color: '#64748b', paddingBottom: 8, borderBottom: '1px solid #e5edf7' }}>
              Grade: {studentInfo.grade_level ?? progress.gradeLevel ?? 'N/A'}
              {studentInfo.relationship_type ? ` • Relationship: ${studentInfo.relationship_type}` : ''}
            </div>
            {!!currentTeachers.length && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#475569' }}>
                <strong style={{ color: '#1e3a5f' }}>Teachers:</strong> {currentTeachers.join(', ')}
              </div>
            )}
            {!!familyMembers.length && (
              <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
                <strong style={{ color: '#1e3a5f', fontSize: 12 }}>Family Members</strong>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10 }}>
                  {familyMembers.map((member, index) => (
                    <div key={`${member.email || member.user_id || member.parent_id}`} style={{ padding: '10px 12px', borderRadius: 10, background: '#ffffff', border: '1px solid #dbe4f0', fontSize: 11, color: '#475569', minHeight: 108, boxShadow: '0 6px 14px rgba(15, 23, 42, 0.05)' }}>
                      <div style={{ fontWeight: 800, color: '#64748b', marginBottom: 6 }}>Guardian #{index + 1}</div>
                      <div style={{ lineHeight: 1.45, fontWeight: 700, color: '#1e3a5f' }}>{`${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Family Member'}</div>
                      <div style={{ lineHeight: 1.45, marginTop: 3 }}>{member.relationship_type || 'parent'}</div>
                      <div style={{ lineHeight: 1.45, marginTop: 3 }}>{member.email || 'No email listed'}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600, color: '#374151', fontSize: 16 }}>Legend:</span>
        <GradingLegend />
        {canEdit && <span style={{ fontSize: 13, color: '#6b7280', fontStyle: 'italic' }}>Click any tile to enter a data point</span>}
        {!canEdit && <span style={{ fontSize: 13, color: '#6b7280', fontStyle: 'italic' }}>Progress dots reflect saved data points for each standard.</span>}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#334155', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={showStandardCodes}
            onChange={(event) => setShowStandardCodes(event.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          Show standard codes
        </label>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap', paddingTop: 12, marginBottom: 18, borderTop: '1px solid #e5e7eb' }}>
        {canEdit && selectedStandardsForEntry.length > 0 && (
          <>
            <button
              type="button"
              onClick={() => navigateToGradeEntry(selectedStandardsForEntry[0], null, selectedStandardsForEntry)}
              style={{ padding: '10px 18px', background: '#166534', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
            >
              Enter data for {selectedStandardsForEntry.length} standards
            </button>
            <button
              type="button"
              onClick={() => setSelectedStandardsForEntry([])}
              style={{ padding: '10px 14px', background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
            >
              Clear selection
            </button>
          </>
        )}
        <button
          type="button"
          onClick={handleExportWorkbook}
          style={{ padding: '10px 18px', background: '#1e3a8a', color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer', boxShadow: '0 2px 6px rgba(30, 58, 138, 0.25)' }}
        >
          Export Excel Report
        </button>
        <button
          type="button"
          onClick={handleExportPdf}
          style={{ padding: '10px 18px', background: '#fb923c', color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer', boxShadow: '0 2px 6px rgba(251, 146, 60, 0.3)' }}
        >
          Generate Report
        </button>
        {(effectiveRole === 'admin' || effectiveRole === 'school_admin' || effectiveRole === 'district_admin' || effectiveRole === 'teacher' || effectiveRole === 'special_services_teacher') && (
          <button
            type="button"
            onClick={() => {
              const prefix = (effectiveRole === 'admin' || effectiveRole === 'school_admin' || effectiveRole === 'district_admin') ? '/admin' : '/teacher';
              navigate(`${prefix}/student-progress-family/${studentId}${classId ? `/${classId}` : ''}`, {
                state: {
                  student: studentInfo,
                  returnTo: {
                    pathname: location.pathname,
                    label: `← Back to Student Progress`,
                    state: location.state,
                  },
                },
              });
            }}
            style={{ padding: '10px 18px', background: '#f8fafc', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer', boxShadow: '0 2px 6px rgba(29, 78, 216, 0.15)' }}
          >
            👨‍👩‍👧 View as Family
          </button>
        )}
      </div>
      <StandardSearch value={standardSearch} onChange={setStandardSearch} />
      {searchingStandards && <p role="status">
        {displayCategories.reduce((total, category) => total + category.visibleSkills.reduce((count, skill) => count + skill.visibleStandards.length, 0), 0)} matching standards in this subject and visible grades.
      </p>}
      {displayCategories.length === 0 && (
        <div style={{ padding: '18px 20px', background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10, color: '#475569', fontSize: 14, textAlign: 'center' }}>
          {searchingStandards ? 'No standards match your search in this subject and visible grades. Try another term or clear the search.'
            : viewMode === 'preferred' && effectiveVisibleCodes
            ? `The selected standard set has no ${selectedSubject || 'subject'} standards for Grades ${visibleProfileColumnKeys.join(', ')}.`
            : `There are no ${selectedSubject || ''} standards for Grades ${visibleProfileColumnKeys.join(', ')}. Try another grade or subject.`}
        </div>
      )}
      {displayCategories.map((cat) => {
        const domainStyle = assessmentSummaryStyle(cat.skills.flatMap(skill => skill.standards)
          .filter(standard => standardAppliesToGrade(standard, activeGrade)
            && (viewMode !== 'preferred' || !effectiveVisibleCodes || effectiveVisibleCodes.has(standard.mls_code))));
        const domainText = domainStyle === GRADING_STYLES.green || domainStyle === GRADING_STYLES.red
          ? '#fff' : domainStyle.text;
        return (
        <div key={cat.name} style={{ marginBottom: 28 }}>
          <div
            title={`${cat.name} · ${domainStyle.label}`}
            style={{ background: domainStyle.bg, color: domainText, borderRadius: 10, padding: '10px 18px', cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)' }}
            onClick={() => setExpandedDomains((prev) => ({ ...prev, [cat.name]: !prev[cat.name] }))}
          >
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.1 }}>{cat.name}</div>
              {cat.description && cat.description !== cat.name && (
                <div style={{ fontSize: 11, fontWeight: 500, color: 'inherit', marginTop: 1, letterSpacing: 0.1 }}>
                  {cat.description}
                </div>
              )}
            </div>
            <span style={{ fontSize: 16, fontWeight: 800, lineHeight: 1 }}>
              {searchingStandards || expandedDomains[cat.name] ? '▲' : '▼'}
            </span>
          </div>
          {(searchingStandards || expandedDomains[cat.name]) && cat.visibleSkills.map((skill) => {
            const visibleCols = skill.visibleCols;
            const skillKey = `${cat.name}::${skill.label}`;
            const expanded = searchingStandards || expandedSkills[skillKey] || false;
            const strandStyle = assessmentSummaryStyle((skill.standards || [])
              .filter(standard => standardAppliesToGrade(standard, activeGrade)
                && (viewMode !== 'preferred' || !effectiveVisibleCodes || effectiveVisibleCodes.has(standard.mls_code))));
            const strandText = strandStyle === GRADING_STYLES.green || strandStyle === GRADING_STYLES.red
              ? '#fff' : strandStyle.text;

            return (
              <div key={skill.label} style={{ marginBottom: 16, marginTop: 6, marginLeft: 'clamp(20px, 3vw, 32px)', minWidth: 0 }}>
                <div
                  title={`${skill.label} · ${strandStyle.label}`}
                  style={{ background: strandStyle.bg, color: strandText, fontWeight: 800, fontSize: 15, padding: '6px 14px', borderRadius: 8, cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap', boxShadow: 'inset 0 0 0 1px rgba(15,23,42,0.08)' }}
                  onClick={() => setExpandedSkills(prev => ({ ...prev, [skillKey]: !prev[skillKey] }))}
                >
                  <span style={{ fontSize: 15, fontWeight: 800 }}>{skill.label}</span>
                  <span style={{ marginLeft: 4, fontSize: 12 }}>{expanded ? '▲' : '▼'}</span>
                </div>
                {expanded && (
                  <div style={{ overflowX: 'auto', border: '1px solid #d6deea', borderTop: 'none', background: '#fcfcfd', borderBottomLeftRadius: 10, borderBottomRightRadius: 10, padding: '10px 10px 12px' }}>
                    <nav aria-label={`${skill.label} grade navigation`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '8px', marginBottom: 10, background: '#f3f7ff', border: '1px solid #d8e6ff', borderRadius: 8 }}>
                      <button type="button" aria-label="Previous grade" disabled={activeColumnIndex === 0}
                        style={{ border: '1px solid #c6d5f0', borderRadius: 8, padding: '6px 14px', background: '#fff', fontSize: 22, color: '#27459b', opacity: activeColumnIndex === 0 ? 0.4 : 1 }}
                        onClick={() => moveProfileGrade(-1)}>‹</button>
                      <div style={{ textAlign: 'center', color: '#27459b', fontWeight: 800 }}>
                        Grades {visibleProfileColumnKeys.join(', ')}
                        {focusedProfileColumnKey !== activeGrade && (
                          <div style={{ fontSize: 11, fontWeight: 700 }}>Grade {activeGrade} · {formatGradeColumnLabel(focusedProfileColumnKey)}</div>
                        )}
                        <div style={{ fontSize: 11, fontWeight: 400 }}>Use arrows to change grades</div>
                      </div>
                      <button type="button" aria-label="Next grade" disabled={activeColumnIndex >= profileGradeColumns.length - 1}
                        style={{ border: '1px solid #c6d5f0', borderRadius: 8, padding: '6px 14px', background: '#fff', fontSize: 22, color: '#27459b', opacity: activeColumnIndex >= profileGradeColumns.length - 1 ? 0.4 : 1 }}
                        onClick={() => moveProfileGrade(1)}>›</button>
                    </nav>
                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, visibleCols.length)}, minmax(160px, 1fr))`, gap: 10, minWidth: Math.max(visibleCols.length * 160, 300) }}>
                      {visibleCols.map((grade) => {
                        const standardsForGrade = (skill.visibleStandards || []).filter((standard) => getProfileColumnKey(standard) === grade);
                        const gradeStats = calculateAssessmentStats(standardsForGrade);
                        const isFullyMastered = gradeStats.total > 0 && gradeStats.mastered === gradeStats.total;
                        const isPartiallyMastered = gradeStats.mastered > 0 && !isFullyMastered;
                        const selectableStandardsForGrade = canEdit
                          ? standardsForGrade.filter((standard) => !getAssessmentGate(
                              standard,
                              studentInfo?.grade_level ?? progress?.gradeLevel,
                              masteryBySubjectGrade,
                            ).locked)
                          : [];
                        const selectedGradeStandardCodes = new Set(
                          selectedStandardsForEntry.map((standard) => standard.mls_code)
                        );
                        const selectedGradeCount = selectableStandardsForGrade.filter(
                          (standard) => selectedGradeStandardCodes.has(standard.mls_code)
                        ).length;
                        const allGradeStandardsSelected = selectableStandardsForGrade.length > 0
                          && selectedGradeCount === selectableStandardsForGrade.length;
                        const someGradeStandardsSelected = selectedGradeCount > 0 && !allGradeStandardsSelected;

                        const clusters = [];
                        const byCluster = new Map();

                        standardsForGrade.forEach((standard) => {
                          const clusterTitle = resolveClusterLabel(standard);
                          const clusterKey = `${String(standard.cluster_and_expectation_number || '').trim().toLowerCase()}::${clusterTitle.toLowerCase()}`;
                          if (!byCluster.has(clusterKey)) {
                            const clusterObj = { key: clusterKey, title: clusterTitle, standards: [] };
                            byCluster.set(clusterKey, clusterObj);
                            clusters.push(clusterObj);
                          }
                          byCluster.get(clusterKey).standards.push(standard);
                        });

                        return (
                          <section key={`${skillKey}-${grade}`} aria-label={`${formatGradeColumnLabel(grade)} standards`} style={{ border: grade === focusedProfileColumnKey ? '2px solid #2563eb' : '1px solid #d8e6ff', borderRadius: 10, background: '#ffffff', overflow: 'hidden', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.08)' }}>
                            {isFullyMastered ? (
                              <div style={{ padding: '8px 6px', textAlign: 'center', color: '#ffffff', background: '#22c55e', fontWeight: 800, fontSize: 13, borderBottom: '1px solid #16a34a', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, boxShadow: 'inset 0 -2px 0 rgba(0,0,0,0.1)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 900 }}>
                                  <span>{formatGradeColumnLabel(grade)}</span>
                                  <span>✓</span>
                                </div>
                                <div style={{ fontSize: 10, fontWeight: 800, opacity: 0.95, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                  All Proficient ({gradeStats.mastered}/{gradeStats.total})
                                </div>
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 1 }}>
                                  {gradeStats.green > 0 && <span style={{ background: 'rgba(255,255,255,0.25)', color: '#fff', padding: '1px 5px', borderRadius: 999, fontSize: 9, fontWeight: 800 }}>🟢{gradeStats.green}</span>}
                                  {gradeStats.yellow > 0 && <span style={{ background: '#ffd84d', color: '#5b4a00', padding: '1px 5px', borderRadius: 999, fontSize: 9, fontWeight: 800 }}>🟡{gradeStats.yellow}</span>}
                                  {gradeStats.red > 0 && <span style={{ background: '#ff7a79', color: '#fff', padding: '1px 5px', borderRadius: 999, fontSize: 9, fontWeight: 800 }}>🔴{gradeStats.red}</span>}
                                </div>
                              </div>
                            ) : isPartiallyMastered ? (
                              <div style={{ position: 'relative', padding: '8px 6px', textAlign: 'center', color: '#1e3a8a', background: '#edf3ff', fontWeight: 800, fontSize: 13, borderBottom: '1px solid #d8e6ff', overflow: 'hidden' }}>
                                <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: `${gradeStats.masteryPercent}%`, background: 'linear-gradient(90deg, #bbf7d0 0%, #86efac 100%)', transition: 'width 0.3s ease', zIndex: 0 }} />
                                <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                  <div style={{ fontWeight: 900, color: '#166534' }}>{formatGradeColumnLabel(grade)}</div>
                                  <div style={{ fontSize: 10, fontWeight: 800, color: '#15803d', letterSpacing: '0.02em' }}>
                                    {gradeStats.mastered}/{gradeStats.total} Proficient ({gradeStats.masteryPercent}%)
                                  </div>
                                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 1 }}>
                                    {gradeStats.green > 0 && <span style={{ background: '#22c55e', color: '#fff', padding: '1px 5px', borderRadius: 999, fontSize: 9, fontWeight: 800 }}>🟢{gradeStats.green}</span>}
                                    {gradeStats.lightGreen > 0 && <span style={{ background: GRADING_STYLES.lightgreen.bg, color: '#12203a', padding: '1px 5px', borderRadius: 999, fontSize: 9, fontWeight: 800 }}>{gradeStats.lightGreen} On Track</span>}
                                    {gradeStats.yellow > 0 && <span style={{ background: '#ffd84d', color: '#5b4a00', padding: '1px 5px', borderRadius: 999, fontSize: 9, fontWeight: 800 }}>🟡{gradeStats.yellow}</span>}
                                    {gradeStats.red > 0 && <span style={{ background: '#ff7a79', color: '#fff', padding: '1px 5px', borderRadius: 999, fontSize: 9, fontWeight: 800 }}>🔴{gradeStats.red}</span>}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div style={{ padding: '8px 6px', textAlign: 'center', color: '#1e3a8a', background: '#edf3ff', fontWeight: 800, fontSize: 13, borderBottom: '1px solid #d8e6ff', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                <div style={{ fontWeight: 800 }}>{formatGradeColumnLabel(grade)}</div>
                                <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b' }}>
                                  0/{gradeStats.total} Proficient
                                </div>
                                {gradeStats.lightGreen > 0 && <span style={{ background: GRADING_STYLES.lightgreen.bg, color: '#12203a', padding: '1px 5px', borderRadius: 999, fontSize: 9, fontWeight: 800 }}>{gradeStats.lightGreen} On Track</span>}
                              </div>
                            )}
                            {canEdit && selectableStandardsForGrade.length > 0 && (
                              <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, minHeight: 32, padding: '6px 8px', background: '#f8fafc', borderBottom: '1px solid #d8e6ff', color: '#334155', fontSize: 10, fontWeight: 800, cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  aria-label={`Select all standards in ${formatGradeColumnLabel(grade)}`}
                                  checked={allGradeStandardsSelected}
                                  ref={(element) => {
                                    if (element) element.indeterminate = someGradeStandardsSelected;
                                  }}
                                  onChange={(event) => {
                                    const gradeCodes = new Set(selectableStandardsForGrade.map((standard) => standard.mls_code));
                                    setSelectedStandardsForEntry((current) => {
                                      const withoutGradeStandards = current.filter((standard) => !gradeCodes.has(standard.mls_code));
                                      return event.target.checked
                                        ? [...withoutGradeStandards, ...selectableStandardsForGrade]
                                        : withoutGradeStandards;
                                    });
                                  }}
                                  style={{ width: 14, height: 14, margin: 0, cursor: 'pointer' }}
                                />
                                Select all {selectableStandardsForGrade.length} standards
                              </label>
                            )}
                            <div style={{ padding: 8, display: 'grid', gap: 8 }}>
                              {standardsForGrade.length === 0 && (
                                <div style={{ padding: 12, textAlign: 'center', color: '#64748b', fontSize: 12 }}>
                                  No standards for this grade.
                                </div>
                              )}
                              {clusters.map((cluster) => (
                                <div key={`${skillKey}-${grade}-${cluster.key}`} style={{ border: '1px solid #c8d8f6', borderRadius: 8, background: 'linear-gradient(180deg, #f7fbff 0%, #eff6ff 100%)', overflow: 'hidden' }}>
                                  <div style={{ margin: 0, padding: '7px 10px', borderBottom: '1px solid #bfdbfe', background: '#dbeafe', color: '#1e3a8a', fontSize: 12, fontWeight: 800, lineHeight: 1.2, textAlign: 'center' }} title={cluster.title}>
                                    {cluster.title}
                                  </div>
                                  <div style={{ padding: 8, display: 'grid', gap: 8 }}>
                                    {cluster.standards.map((std) => {
                                      const gate = getAssessmentGate(std, studentInfo?.grade_level ?? progress?.gradeLevel, masteryBySubjectGrade);
                                      const isLockedForAssessment = canEdit && gate.locked;
                                      let bg; let color; let border;
                                      if (std.status === 'proficient') { bg = GRADING_STYLES.green.bg; color = GRADING_STYLES.green.text; border = '1px solid rgba(0,0,0,0.04)'; }
                                      else if (std.status === 'on_track') { bg = GRADING_STYLES.lightgreen.bg; color = GRADING_STYLES.lightgreen.text; border = '1px solid rgba(0,0,0,0.04)'; }
                                      else if (std.status === 'working_level') { bg = GRADING_STYLES.yellow.bg; color = GRADING_STYLES.yellow.text; border = '1px solid #f1bf22'; }
                                      else if (std.status === 'not_yet') { bg = GRADING_STYLES.red.bg; color = GRADING_STYLES.red.text; border = '1px solid rgba(0,0,0,0.04)'; }
                                      else { bg = '#eceff4'; color = '#98a2b3'; border = '1px solid #d7dde7'; }

                                      return (
                                        <div key={`${skillKey}-${grade}-${std.mls_code}`} style={{ minWidth: 0 }}>
                                        <div
                                          onClick={() => {
                                            if (!canEdit || isLockedForAssessment) return;
                                            navigateToGradeEntry(std);
                                          }}
                                          title={canEdit ? (isLockedForAssessment ? gate.reason : 'Click to enter data point') : ''}
                                          style={{ minHeight: 86, borderRadius: 8, background: bg, color, border, fontWeight: 700, fontSize: 12, lineHeight: 1.35, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: canEdit && !isLockedForAssessment ? 'pointer' : 'default', boxShadow: std.status !== 'not_assessed' ? '0 1px 3px rgba(15, 23, 42, 0.16)' : 'none', transition: 'box-shadow 0.2s, transform 0.15s', outline: 'none', position: 'relative', padding: canEdit ? '30px 10px 12px' : '12px 10px', textAlign: 'center', boxSizing: 'border-box', gap: 8, opacity: isLockedForAssessment ? 0.55 : 1 }}
                                        >
                                          {canEdit && !isLockedForAssessment && (
                                            <label
                                              onClick={(event) => event.stopPropagation()}
                                              style={{ position: 'absolute', top: 5, left: 5, zIndex: 1, display: 'flex', alignItems: 'center', gap: 3, padding: '3px 5px', borderRadius: 5, background: 'rgba(255,255,255,0.9)', color: '#334155', fontSize: 9, fontWeight: 700, cursor: 'pointer' }}
                                            >
                                              <input
                                                type="checkbox"
                                                aria-label={`Select ${std.mls_code} for data entry`}
                                                checked={selectedStandardsForEntry.some((item) => item.mls_code === std.mls_code)}
                                                onChange={(event) => setSelectedStandardsForEntry((current) => (
                                                  event.target.checked
                                                    ? [...current.filter((item) => item.mls_code !== std.mls_code), std]
                                                    : current.filter((item) => item.mls_code !== std.mls_code)
                                                ))}
                                                style={{ width: 12, height: 12, margin: 0 }}
                                              />
                                              Select
                                            </label>
                                          )}
                                          {isLockedForAssessment && (
                                            <span style={{ position: 'absolute', top: 6, right: 6, fontSize: 10, background: '#0f172a', color: '#ffffff', borderRadius: 999, padding: '2px 6px', lineHeight: 1 }}>
                                              Locked
                                            </span>
                                          )}
                                          {showStandardCodes && !!std.mls_code && (
                                            <span style={{ display: 'block', width: '100%', fontSize: 11, fontWeight: 800, letterSpacing: '0.02em', opacity: 0.9 }}>
                                              {std.mls_code}
                                            </span>
                                          )}
                                          <span style={{ display: 'block', width: '100%', fontSize: 12, lineHeight: 1.35, whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                                            {std.text}
                                          </span>
                                          {!!std.data_points?.length && (
                                            <div
                                              style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                justifyContent: 'center',
                                                gap: 4,
                                                marginTop: 'auto',
                                                maxWidth: '100%',
                                                background: 'rgba(15, 23, 42, 0.16)',
                                                borderRadius: 999,
                                                padding: '4px 6px',
                                                boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.35)',
                                              }}
                                            >
                                              {std.data_points.slice(-12).map((point, pointIndex) => {
                                                const dotColor = pointColor(point);
                                                const dotBg = (GRADING_STYLES[dotColor] || GRADING_STYLES.gray).bg;
                                                const dotText = (GRADING_STYLES[dotColor] || GRADING_STYLES.gray).text;
                                                return (
                                                  <span
                                                    key={`${std.mls_code}-point-${point.id || pointIndex}`}
                                                    onMouseEnter={(event) => {
                                                      cancelDataPointHoverClose();
                                                      setHoveredDataPoint({ point, standard: std, rect: event.currentTarget.getBoundingClientRect() });
                                                    }}
                                                    onMouseLeave={scheduleDataPointHoverClose}
                                                    onClick={(event) => {
                                                      event.stopPropagation();
                                                      if (!canEdit) return;
                                                      navigateToGradeEntry(std, point.id || null);
                                                    }}
                                                    aria-label={`${point.title || 'Data point'} for ${std.mls_code}${point.accommodations_applied ? `, ${ADDITIONAL_SUPPORT_LABEL}` : ''}`}
                                                    style={{ width: 14, height: 14, borderRadius: '50%', background: dotBg, color: dotText, fontSize: 8, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: point.accommodations_applied ? `0 0 0 2px ${ADDITIONAL_SUPPORT_COLOR}` : '0 0 0 1.5px rgba(255,255,255,0.85), 0 1px 2px rgba(15,23,42,0.18)', cursor: 'pointer' }}
                                                  >
                                                    {pointIndex + 1}
                                                  </span>
                                                );
                                              })}
                                            </div>
                                          )}
                                          {hoveredDataPoint?.standard.mls_code === std.mls_code && createPortal((() => {
                                            const { point: hoveredPoint, standard, rect } = hoveredDataPoint;
                                            const standardPoints = [...(standard.data_points || [])].reverse();
                                            return (
                                              <div
                                                role="tooltip"
                                                onMouseEnter={cancelDataPointHoverClose}
                                                onMouseLeave={scheduleDataPointHoverClose}
                                                onMouseDown={(event) => event.stopPropagation()}
                                                onClick={(event) => event.stopPropagation()}
                                                style={{ position: 'fixed', zIndex: 10000, left: Math.max(12, Math.min(rect.left, window.innerWidth - 392)), top: Math.max(12, rect.top - 12), transform: 'translateY(-100%)', width: 368, maxWidth: 'calc(100vw - 24px)', maxHeight: 420, overflowY: 'auto', padding: 13, borderRadius: 8, background: '#ffffff', color: '#1e293b', border: '1px solid #cbd5e1', boxShadow: '0 8px 24px rgba(15, 23, 42, 0.2)', fontSize: 12, lineHeight: 1.4, pointerEvents: 'auto', userSelect: 'text', cursor: 'default' }}
                                              >
                                                <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 3 }}>All Data Points for {standard.mls_code}</div>
                                                {standard.text && <div style={{ color: '#475569', marginBottom: 9 }}>{standard.text}</div>}
                                                <div style={{ display: 'grid', gap: 7 }}>
                                                  {standardPoints.map((historyPoint, historyIndex) => {
                                                    const scoreLabel = historyPoint.score !== null && historyPoint.score !== undefined ? `${historyPoint.score}%` : 'No score';
                                                    const statusLabel = (GRADING_STYLES[pointColor(historyPoint)] || GRADING_STYLES.gray).label;
                                                    const isHoveredPoint = historyPoint.id === hoveredPoint.id;
                                                    return (
                                                      <div key={historyPoint.id || historyIndex} className={historyPoint.accommodations_applied ? 'ae-supported-data-point' : undefined} style={{ padding: '8px 9px', borderRadius: 6, border: isHoveredPoint ? '2px solid #2563eb' : '1px solid #dbe4f0', background: isHoveredPoint ? '#eff6ff' : '#f8fafc' }}>
                                                        <div className="ae-data-point-header">
                                                          <div style={{ fontWeight: 800 }}>{historyPoint.title || 'Untitled data point'}</div>
                                                          <AdditionalSupportIndicator applied={historyPoint.accommodations_applied} />
                                                        </div>
                                                        <div><strong>Type:</strong> {dataPointTypeLabel(historyPoint.data_type)}</div>
                                                        <div><strong>Date:</strong> {historyPoint.date || 'Not recorded'} · <strong>Score:</strong> {scoreLabel} <span style={{ color: '#64748b' }}>({statusLabel})</span></div>
                                                        {historyPoint.notes && <div style={{ marginTop: 3, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}><strong>Notes:</strong> {historyPoint.notes}</div>}
                                                      </div>
                                                    );
                                                  })}
                                                </div>
                                              </div>
                                            );
                                          })(), document.body)}
                                        </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </section>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        );
      })}

      <PageBackNavigation onBack={handleBack} label={returnLabel} position="bottom" />
      <BackToTop />
    </div>
  );
}
